import axios from 'axios';
import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators';
import { Form, FormRequest } from '@/models/Form';
import { User } from '@/models/User';
import { FormElement } from '@/models/FormElement';

axios.defaults.baseURL = process.env.VUE_APP_API_URL;

@Module({ namespaced: true })
class FormModule extends VuexModule {
    _all: Form[] = [];

    get all(): Form[] {
        return this._all;
    }

    @Mutation
    add(form: Form) {
        const index = this._all.findIndex((f: Form) => f.uri === form.uri);
        if (index === -1) {
            this._all.push(form);
        }
    }

    @Mutation
    set(form: Form) {
        const index = this._all.findIndex((f: Form) => f.uri === form.uri);
        if (index > -1) {
            this._all.splice(index, 1, form);
        }
    }

    @Mutation
    remove(form: Form) {
        const index = this._all.findIndex((f: Form) => f.uri === form.uri);
        if (index > -1) {
            this._all.splice(index, 1);
        }
    }

    @Mutation
    setElement(element: FormElement) {
        const index = this._all.findIndex((f: Form) => f.uri === element.form);
        if (index > -1) {
            const i = this._all[index].elements.findIndex((e: FormElement) => e.id === element.id);
            if (i > -1) {
                this._all[index].elements.splice(i, 1, element);
            } else {
                this._all[index].elements.push(element);
            }
        }
    }

    @Mutation
    removeElement(element: FormElement) {
        const index = this._all.findIndex((f: Form) => f.uri === element.form);

        if (index > -1) {
            const i = this._all[index].elements.findIndex((e: FormElement) => e.id === element.id);
            this._all[index].elements.splice(i, 1);
        }
    }

    @Action
    async init(users: User[]): Promise<boolean> {
        try {
            const r = await axios({
                method: 'GET',
                url: '/api/forms',
            });
            return r.data['hydra:member'].map((data: any) => {
                const user: any = users.find((u: User) => u.uri === data.owner);

                this.context.commit('add', new Form({ ...data, user }));
            });
        } catch (e) {
            return e;
        }
    }

    @Action
    async getImage(ref: string) {
        try {
            const r = await axios({
                method: 'GET',
                url: ref,
            });
            return axios.defaults.baseURL + r.data.contentUrl;
        } catch (e) {
            return e;
        }
    }

    @Action
    async uploadImage(data: any) {
        try {
            return await axios({
                method: 'POST',
                url: '/api/media_objects',
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                data,
            });
        } catch (e) {
            return e;
        }
    }

    @Action
    async duplicate({ owner, formData }: { owner: User; formData: Form }): Promise<Form> {
        try {
            const r = await axios({
                method: 'POST',
                url: `/api/clone`,
                data: {
                    '@id': `/api/forms/${formData.id}`,
                },
            });
            const form = new Form({ ...r.data, owner });

            this.context.commit('add', form);

            return form;
        } catch (e) {
            return e;
        }
    }

    @Action
    async create({ owner, formData }: { owner: User; formData: FormRequest }) {
        try {
            const r = await axios({
                method: 'POST',
                url: '/api/forms',
                data: formData,
            });
            const form = new Form({ ...r.data, user: owner });

            this.context.commit('add', form);

            return form;
        } catch (e) {
            return e;
        }
    }

    @Action
    async update({ owner, formData }: { owner: User; formData: FormRequest }) {
        try {
            await axios({
                method: 'PUT',
                url: `/api/forms/${formData.id}`,
                responseType: 'json',
                headers: {
                    'accept': 'application/ld+json',
                    'content-type': 'application/ld+json',
                },
                data: formData,
            });
            const r = await axios({
                method: 'GET',
                url: `/api/forms/${formData.id}`,
            });

            if (r.status !== 200) {
                throw Error('Form UPDATE failed.');
            }

            this.context.commit('set', new Form({ ...r.data, user: owner }));
        } catch (e) {
            return e;
        }
    }

    @Action
    async delete(form: Form) {
        try {
            const r = await axios({
                method: 'DELETE',
                url: `/api/forms/${form.id}`,
            });

            if (r.status !== 204) {
                throw Error('Form DELETE failed.');
            }

            this.context.commit('remove', form);
        } catch (e) {
            return e;
        }
    }
}

export default FormModule;
